<script setup lang="ts">
// How the environment's data is classified: detected from the data, or overridden on its access list
// (docs/proposal-environment-access.md). The owner may only make it stricter (raw) or remove an override; relaxing
// it is an admin's exception with a reason, for the backup on disk. The API decides; this offers what the role allows.
import { computed, ref } from 'vue';
import { must } from '../api/client';
import type { Environment } from '../api/types';
import { useContext } from '../context';
import { useSession } from '../stores/session';
import { formatTime } from '../status/status';
import ProblemAlert from './ProblemAlert.vue';
import Icon from './Icon.vue';

type Classification = 'production-raw' | 'sanitized' | 'synthetic';

const props = defineProps<{ env: Environment }>();
const emit = defineEmits<{ changed: [] }>();
const { api } = useContext();
const session = useSession();

const admin = computed(() => props.env.yourRole === 'admin');
const owner = computed(() => props.env.yourRole === 'owner');
const ov = computed(() => props.env.classificationOverride);
const LABEL: Record<string, string> = { 'production-raw': 'raw production', sanitized: 'sanitized', synthetic: 'synthetic' };
const label = (c?: string) => (c ? (LABEL[c] ?? c) : '—');

/** The owner may remove an admin's relaxing exception, or a stricter one they set themselves. */
const mayRemove = computed(() => {
  const o = ov.value;
  if (!o) return false;
  if (admin.value) return true;
  if (!owner.value) return false;
  const me = (session.me?.username ?? '').toLowerCase();
  return o.value !== 'production-raw' || o.setBy.username.toLowerCase() === me;
});
const mayMakeRaw = computed(() => (admin.value || owner.value) && props.env.dataClassification !== 'production-raw');

const editing = ref(false);
const value = ref<Classification>('sanitized');
const reason = ref('');
const until = ref('');
const error = ref<unknown>();
const message = ref<string>();
const formError = ref<string>();
const busy = ref(false);

async function put(v: Classification | null, r?: string, u?: string) {
  error.value = undefined;
  message.value = undefined;
  formError.value = undefined;
  busy.value = true;
  try {
    must(await api.PUT('/environments/{name}/classification', {
      params: { path: { name: props.env.name } },
      body: { value: v, ...(r ? { reason: r } : {}), ...(u ? { until: u } : {}) },
    }));
    message.value = v ? `The data now counts as ${label(v)}.` : 'Override removed; the detected classification counts.';
    editing.value = false;
    emit('changed');
  } catch (e) {
    error.value = e;
  } finally {
    busy.value = false;
  }
}

function saveAdmin() {
  const relaxing = value.value !== 'production-raw';
  if (relaxing && !reason.value.trim()) {
    formError.value = 'Relaxing the classification needs a reason.';
    return;
  }
  // a date from the picker ends at the start of that day, UTC
  const u = relaxing && until.value ? new Date(`${until.value}T00:00:00Z`).toISOString() : undefined;
  void put(value.value, reason.value.trim() || undefined, u);
}
</script>

<template>
  <section class="card" aria-labelledby="class-h" data-testid="classification">
    <div class="card-head">
      <h2 id="class-h">Data classification</h2>
      <span class="muted" style="font-size: 13px">Decides whether the access list alone is enough for logs and actions.</span>
    </div>
    <dl class="facts">
      <dt>Counts as</dt>
      <dd><span class="pill sm" :class="env.dataClassification === 'production-raw' ? 'tone-bad' : 'tone-ok'" data-testid="class-effective">{{ label(env.dataClassification) }}</span></dd>
      <dt>Detected</dt>
      <dd>{{ label(env.detectedClassification) }} <span class="sub" style="display: inline">from the flavour <code>{{ env.db?.flavour ?? env.flavour ?? '—' }}</code></span></dd>
      <template v-if="ov">
        <dt>Override</dt>
        <dd data-testid="class-override">
          <strong>{{ label(ov.value) }}</strong>, set by {{ ov.setBy.username }} {{ formatTime(ov.setAt) }}
          <span v-if="!ov.holds" class="pill sm tone-idle" style="margin-left: 4px">lapsed</span>
          <div v-if="ov.reason" class="sub">“{{ ov.reason }}”</div>
          <div v-if="ov.backupId" class="sub">for backup <code>{{ ov.backupId }}</code> only<template v-if="ov.until">, until {{ formatTime(ov.until) }}</template><template v-if="!ov.holds">: no longer the data on disk, or ended</template></div>
        </dd>
      </template>
    </dl>

    <div class="actions-row" style="margin-top: 1rem">
      <button v-if="mayMakeRaw" type="button" :disabled="busy" data-testid="class-make-raw" @click="put('production-raw')"><Icon name="lock" /> Treat as raw production data</button>
      <button v-if="mayRemove" type="button" :disabled="busy" data-testid="class-remove" @click="put(null)">Remove override</button>
      <button v-if="admin && !editing" type="button" class="primary" data-testid="class-edit" @click="editing = true">Set classification…</button>
    </div>
    <p v-if="owner && !admin" class="hint" style="margin-top: 8px">As the owner you can make it stricter. Treating raw data as sanitized hands it to people without the entitlement, so only an admin can do that, with a reason.</p>

    <form v-if="editing" novalidate style="margin-top: 1rem; display: flex; flex-direction: column; gap: 1rem; padding-top: 1rem; border-top: 1px solid var(--line-soft)" @submit.prevent="saveAdmin">
      <div class="field" style="max-width: 20rem">
        <label for="class-value">Treat the data as</label>
        <select id="class-value" v-model="value" data-testid="class-value">
          <option value="production-raw">raw production (stricter)</option>
          <option value="sanitized">sanitized</option>
          <option value="synthetic">synthetic</option>
        </select>
      </div>
      <template v-if="value !== 'production-raw'">
        <div class="field">
          <label for="class-reason">Reason <span class="req-label">required</span></label>
          <textarea id="class-reason" v-model="reason" rows="2" maxlength="500" style="font-family: inherit" placeholder="What was reviewed, by whom, and why the data is safe to share" data-testid="class-reason"></textarea>
        </div>
        <div class="field" style="max-width: 14rem">
          <label for="class-until">Until <span class="opt-label">optional</span></label>
          <input id="class-until" v-model="until" type="date" />
        </div>
        <p class="notice" style="font-size: 13px">Holds for the backup on disk now (<code>{{ env.db?.backupId ?? 'none restored yet' }}</code>) only: a refresh-data ends it. Every change is audited.</p>
      </template>
      <div class="actions-row">
        <button type="submit" class="primary" :disabled="busy" data-testid="class-save">Save</button>
        <button type="button" @click="editing = false">Cancel</button>
      </div>
    </form>

    <p v-if="formError" class="field-error" role="alert" style="margin-top: 0.5rem">{{ formError }}</p>
    <p v-if="message" class="info" role="status" style="margin-top: 0.75rem">{{ message }}</p>
    <div v-if="error" style="margin-top: 0.75rem"><ProblemAlert :error="error" context="The API refused the change" /></div>
  </section>
</template>
